import { XYPosition } from '@xyflow/react';
import { CanvasEdge, CanvasEdgeTransformations, CanvasGroup, CanvasNode } from '.';
import { CanvasGridPosition } from '../helpers/gridPositioningUtils';
import { FreePosition } from '../../../types/FreePosition';

export enum CanvasEventType {
  AddNode = 'AddNode',
  MoveNode = 'MoveNode',
  MoveStandaloneNode = 'MoveStandaloneNode',
  MoveGroup = 'MoveGroup',
  EscapeFromShadowStep = 'EscapeFromShadowStep',
  RemoveGroup = 'RemoveGroup',
  SwapNodes = 'SwapNodes',
  AddSection = 'AddSection',
  MoveSection = 'MoveSection',
  ResizeSection = 'ResizeSection',
  SectionChangeData = 'SectionChangeData',
  MoveStickyNote = 'MoveStickyNote',
  ResizeStickyNote = 'ResizeStickyNote',
  ChangeStickyNoteData = 'ChangeStickyNoteData',
  RemoveSection = 'RemoveSection',
  UngroupSection = 'UngroupSection',
  AddStickyNote = 'AddStickyNote',
  RemoveStickyNote = 'RemoveStickyNote',
  MoveTextNode = 'MoveTextNode',
  ResizeTextNode = 'ResizeTextNode',
  AddOrChangeTextNode = 'AddOrChangeTextNode',
  RemoveTextNode = 'RemoveTextNode',
  MoveMultipleNodes = 'MoveMultipleNodes',
}

interface CanvasEventAbstract {
  type: CanvasEventType;
}
export type CanvasNodeMove = {
  parentId?: string;
  nodeId: string;
  newPosition: CanvasGridPosition;
};
export interface CanvasAddNodeEvent<GroupMetadata = any> extends CanvasEventAbstract {
  type: CanvasEventType.AddNode;
  id: string;
  originNodeId?: string;
  newNode: CanvasNode;
  newEdge?: CanvasEdge;
  newGroup?: CanvasGroup<GroupMetadata>;
  nodesToMove: CanvasNodeMove[];
  edgeTransformations: CanvasEdgeTransformations;
  parentCanvasNodePosition?: CanvasGridPosition;
}

export interface CanvasMoveNodeEvent extends CanvasEventAbstract {
  type: CanvasEventType.MoveNode;
  parentId?: string;
  nodeId: string;
  newPosition: CanvasGridPosition;
}

export interface CanvasMoveGroupEvent extends CanvasEventAbstract {
  type: CanvasEventType.MoveGroup;
  parentId?: string;
  nodeId: string;
  newPosition: CanvasGridPosition;
}

export interface CanvasMoveStandaloneNodeEvent extends CanvasEventAbstract {
  type: CanvasEventType.MoveStandaloneNode;
  nodeId: string;
  newPosition: CanvasGridPosition;
  parentId?: string;
}

export interface CanvasEscapeFromTempNodeEvent extends CanvasEventAbstract {
  type: CanvasEventType.EscapeFromShadowStep;
  nodeId: string;
  parentId: string;
}

export interface CanvasRemoveGroupEvent extends CanvasEventAbstract {
  type: CanvasEventType.RemoveGroup;
  groupId: string;
}

export interface CanvasSwapNodesEvent extends CanvasEventAbstract {
  type: CanvasEventType.SwapNodes;
  sourceNode: {
    id: string;
    parentId?: string;
  };
  targetNode: {
    id: string;
    parentId?: string;
  };
}

export interface CanvasAddSectionEvent extends CanvasEventAbstract {
  type: CanvasEventType.AddSection;
  id: string;
  xyPosition: XYPosition;
  width: number;
  height: number;
  color: string;
  name: string;
  /**
   * Nodes that will be moved into this section upon creation.
   * This array contains objects with node IDs and their grid positions that should become children of the section.
   */
  nodesToMove?: Array<{
    id: string;
    position: CanvasGridPosition;
    xyPosition: FreePosition;
  }>;
}

export interface CanvasMoveSectionEvent extends CanvasEventAbstract {
  type: CanvasEventType.MoveSection;
  id: string;
  xyPosition: XYPosition;
}

export interface CanvasResizeSectionEvent {
  type: CanvasEventType.ResizeSection;
  id: string;
  width: number;
  height: number;
  xyPosition: XYPosition;
}

export interface CanvasChangeSectionEvent {
  type: CanvasEventType.SectionChangeData;
  id: string;
  color: string;
  name: string;
}

export interface CanvasMoveStickyNoteEvent {
  type: CanvasEventType.MoveStickyNote;
  id: string;
  xyPosition: XYPosition;
  parentId?: string;
}

export interface CanvasResizeStickyNoteEvent {
  type: CanvasEventType.ResizeStickyNote;
  id: string;
  width: number;
  height: number;
  xyPosition: XYPosition;
}

export interface CanvasChangeStickyNoteEvent {
  type: CanvasEventType.ChangeStickyNoteData;
  id: string;
  color: string;
  richText: string;
  xyPosition: XYPosition;
  parentId?: string;
}

export interface CanvasRemoveSectionEvent {
  type: CanvasEventType.RemoveSection;
  id: string;
}

export interface CanvasUngroupSectionEvent {
  type: CanvasEventType.UngroupSection;
  id: string;
  nodesToMove: Array<{
    nodeId: string;
    newPosition: CanvasGridPosition;
    xyPosition: XYPosition;
    type: string;
  }>;
}

export interface CanvasAddStickyNoteEvent {
  type: CanvasEventType.AddStickyNote;
  id: string;
  xyPosition: XYPosition;
  backgroundColor: string;
  richText: string;
  width: number;
  height: number;
  parentId?: string;
}

export interface CanvasRemoveStickyNoteEvent {
  type: CanvasEventType.RemoveStickyNote;
  id: string;
}

export interface CanvasMoveTextNodeEvent {
  type: CanvasEventType.MoveTextNode;
  id: string;
  xyPosition: XYPosition;
  parentId?: string;
}

export interface CanvasResizeTextNodeEvent {
  type: CanvasEventType.ResizeTextNode;
  id: string;
  width: number;
  height: number;
  xyPosition: XYPosition;
}

export interface CanvasAddOrChangeTextNodeEvent {
  type: CanvasEventType.AddOrChangeTextNode;
  id: string;
  richText: string;
  xyPosition: XYPosition;
  parentId?: string;
  width: number;
  height: number;
}

export interface CanvasRemoveTextNodeEvent {
  type: CanvasEventType.RemoveTextNode;
  id: string;
}

export interface CanvasMoveMultipleNodesEvent {
  type: CanvasEventType.MoveMultipleNodes;
  moveEvents: Array<
    | CanvasMoveNodeEvent
    | CanvasMoveGroupEvent
    | CanvasMoveStandaloneNodeEvent
    | CanvasMoveSectionEvent
    | CanvasMoveStickyNoteEvent
    | CanvasMoveTextNodeEvent
  >;
}

export type CanvasEvent =
  | CanvasAddNodeEvent
  | CanvasMoveNodeEvent
  | CanvasMoveGroupEvent
  | CanvasMoveStandaloneNodeEvent
  | CanvasEscapeFromTempNodeEvent
  | CanvasRemoveGroupEvent
  | CanvasSwapNodesEvent
  | CanvasAddSectionEvent
  | CanvasMoveSectionEvent
  | CanvasResizeSectionEvent
  | CanvasChangeSectionEvent
  | CanvasMoveStickyNoteEvent
  | CanvasResizeStickyNoteEvent
  | CanvasChangeStickyNoteEvent
  | CanvasRemoveSectionEvent
  | CanvasUngroupSectionEvent
  | CanvasAddStickyNoteEvent
  | CanvasRemoveStickyNoteEvent
  | CanvasMoveTextNodeEvent
  | CanvasResizeTextNodeEvent
  | CanvasAddOrChangeTextNodeEvent
  | CanvasRemoveTextNodeEvent
  | CanvasMoveMultipleNodesEvent;
